import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

import { CompanyProviderContext } from "./context";
import { getFragmentData } from "../../graphql";
import {
  CompaniesDocument,
  CompanyFragment,
  CompanyFragmentDoc,
} from "../../graphql/graphql";

export type Company = Pick<CompanyFragment, "id" | "name">;

/**
 * Type guard for the Company type.
 */
const isCompany = (value: unknown): value is Company =>
  value !== null &&
  typeof value === "object" &&
  "id" in value &&
  typeof value.id === "string" &&
  "name" in value &&
  typeof value.name === "string";

interface CompanyProviderProps {
  children: React.ReactNode;
}

/**
 * The key for the local storage item that stores the current company.
 */
const LOCAL_STORAGE_KEY_CURRENT_COMPANY = "prenode-ui-current-company";

/**
 * Synchronize the company in the state with the local storage key
 */
const localStorageSetCurrentCompany = (currentCompany: Company | undefined) => {
  if (currentCompany) {
    localStorage.setItem(
      LOCAL_STORAGE_KEY_CURRENT_COMPANY,
      JSON.stringify(currentCompany),
    );
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEY_CURRENT_COMPANY);
  }
};

/**
 * Try to load the initial current company from the local storage key
 * */
const loadCompanyFromLocalStorage = () => {
  try {
    const storedCompany = localStorage.getItem(
      LOCAL_STORAGE_KEY_CURRENT_COMPANY,
    );
    if (storedCompany) {
      const parsedCompany = JSON.parse(storedCompany) as unknown;
      if (isCompany(parsedCompany)) {
        return parsedCompany;
      }
    }
    return undefined;
  } catch (error) {
    console.error(
      "Failed to load the current company from the local storage",
      error,
    );
    return undefined;
  }
};

export const CompanyProvider = ({ children }: CompanyProviderProps) => {
  const { data } = useQuery(CompaniesDocument);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompany] = useState<Company | undefined>(
    loadCompanyFromLocalStorage,
  );

  useEffect(() => {
    localStorageSetCurrentCompany(currentCompany);
  }, [currentCompany]);

  useEffect(() => {
    // Set the companies (and the company if it's not set yet) when the data is available
    if (data) {
      const companies = data.companies.map((company) =>
        getFragmentData(CompanyFragmentDoc, company),
      );
      setCompanies(companies);
      if (
        currentCompany === undefined ||
        // Reset currentCompany if user has no access to it anymore.
        !companies.find((company) => company.id === currentCompany.id)
      ) {
        // NOTE: setCurrentCompany also sets the localStorage via useEffect
        setCurrentCompany(companies[0]);
      }
    }
  }, [data, currentCompany]);

  const value = {
    currentCompany,
    companies,
    setCurrentCompany: (newCompany: Company | undefined) => {
      // NOTE: setCurrentCompany also sets the localStorage via useEffect
      setCurrentCompany(newCompany);
      // TODO: redirect to root
    },
  };

  return (
    <CompanyProviderContext.Provider value={value}>
      {children}
    </CompanyProviderContext.Provider>
  );
};
